<template>
    <div class="h-full flex flex-col relative">
        <div 
            v-if="loading"
            class="absolute flex items-center justify-center top-0 bottom-0 right-0 left-0 z-10"
        >
            <div class="absolute top-0 bottom-0 right-0 left-0 bg-white opacity-75"></div>            
            <div class="z-10 flex items-center"><app-spinner class="text-blue-500" /> <span class="ml-2">{{ loadingText }}</span></div>
        </div>
        <div v-if="showHeader" class="flex items-center justify-between p-3 h-16 bg-blue-600">
            <div v-if="!showSearch" class="flex items-center">
                <template v-for="action in generalActions">
                    <div 
                        class="cursor-pointer rounded-full bg-gray-300 hover:bg-gray-400 p-2 mr-2 shadow"
                        @click="action.handler(selected)"
                        v-tooltip.bottom="action.label"
                    >
                        <component :is="action.icon"/>
                    </div>
                </template>
                <div 
                    class="cursor-pointer rounded-full bg-gray-300 hover:bg-gray-400 p-2 mr-2 shadow"
                    v-tooltip.bottom="'Refresh'"
                    @click="() => { init() }"
                >
                    <refresh-icon />
                </div>                
                <a 
                    @click="downloadExcel" 
                    v-tooltip.top="'Download Excel'"
                    class="cursor-pointer rounded-full bg-gray-300 hover:bg-gray-400 p-2 mr-2 shadow"
                >
                    <download-icon />
                </a>
                <a 
                    :href="pdfUrl"
                    target="_blank"
                    v-if="pdf" 
                    v-tooltip.top="'Download PDF'"
                    class="cursor-pointer rounded-full bg-gray-300 hover:bg-gray-400 p-2 mr-2 shadow text-gray-800"
                >
                    <download-pdf-icon />
                </a>
                <div 
                    class="cursor-pointer rounded-full bg-gray-300 hover:bg-gray-400 p-2 mr-2 md:hidden shadow"
                    v-tooltip.bottom="'Search'"
                    @click="showSearch = true"
                    v-if="searchable"
                >
                    <search-icon />
                </div>
            </div>
            <div 
                class="cursor-pointer rounded-full bg-gray-300 hover:bg-gray-400 p-2 mr-2 md:hidden"
                v-tooltip.bottom="'Back'"
                @click="showSearch = false"
                v-if="showSearch"
            >
                <back-arrow-icon />
            </div>
            <div class="flex items-center flex-1 justify-end">
                <date-picker 
                    v-model="dateFilter"
                    format="MMM DD, YYYY"
                    :value-type="dateFilterValueType"
                    placeholder="Date Filter" 
                    range 
                    class="mr-3"
                    style="width: 230px;"
                    v-if="dateFilterable"
                />
                <div
                    class="items-center justify-between w-full md:w-auto border border-b-0 px-3 py-2 leading-none rounded shadow bg-white"
                    :class="{'hidden md:flex': !showSearch, 'flex': showSearch}"
                    v-if="searchable"
                >
                    <input 
                        v-model="q"
                        type="text" 
                        :placeholder="searchPlaceholder"
                        @keyup="handleSearch"
                        @paste="handleSearch"
                        class="flex-1"
                    >
                    <close-icon 
                        class="ml-2 cursor-pointer text-gray-600 outline-none"
                        :class="{invisible: !q}"
                        width="20"
                        height="20"
                        v-tooltip.bottom="'Clear'"
                        @click.native="() => {
                            q = null;
                            $emit('search', q);
                            $set(localAjaxParams, 'q', q);
                            init();
                        }"
                    />
                </div>
            </div>        
        </div>
        <div ref="table" class="flex-1"></div>
        <vue-context ref="menu">              
            <li 
                class="cursor-default flex items-center px-5 py-1 hover:bg-gray-300"
                @click="copy($event)"
            >
                <file-copy-outlined-icon
                    width="20" 
                    height="20" 
                    class="text-gray-600"
                />
                <span class="ml-3">Copy</span>
            </li>          
        </vue-context>
        <vue-context ref="actions">            
            <template v-for="(action, index) in specificActions">
                <li 
                    v-if="selected.length && ((typeof action.isEligible === 'function' && action.isEligible(selected)) || typeof action.isEligible === 'undefined')"
                    class="cursor-default flex items-center px-5 py-1 hover:bg-gray-300"
                    @click="action.handler(selected)"
                    :key="index"
                >
                    <component 
                        :is="action.icon"
                        width="20" 
                        height="20" 
                        class="text-gray-600"
                    />                
                    <span class="ml-3">{{ action.label }}</span>
                </li>
            </template>         
        </vue-context>
        <input 
            type="text" 
            class="absolute" 
            style="left:-9999px;" 
            ref="clipboardInput" 
        />
    </div>
</template>

<script>
    import Vue from 'vue';
    import Tabulator from 'tabulator-tables';
    import DatePicker from '@dluague/vue-datepicker';
    import { VueContext } from 'vue-context';
    import { mapGetters } from 'vuex';
    import debounce from 'lodash/debounce';
    import MoreIcon from '@/icons/MoreHorizontal.vue';

    export default {
        components: {
            ArrowLeftIcon: () => import('@/icons/ArrowLeft.vue'),
            ArrowRightIcon: () => import('@/icons/ArrowRight.vue'),
            SearchIcon: () => import('@/icons/Search.vue'),
            BackArrowIcon: () => import('@/icons/ArrowBack.vue'),
            DownloadIcon: () => import('@/icons/CloudDownloadOutlined.vue'),
            DownloadPdfIcon: () => import('@/icons/PictureAsPDF.vue'),
            FileCopyOutlinedIcon: () => import('@/icons/FileCopyOutlined.vue'),
            RefreshIcon: () => import('@/icons/Refresh.vue'),
            CloseIcon: () => import('@/icons/Close.vue'),
            CsvIcon: () => import('@/icons/FileCSVSolid.vue'),
            ExcelIcon: () => import('@/icons/FileExcelSolid.vue'),
            VueContext,
            DatePicker,
        },
        props: {
            columns: {
                type: Array,
                required: true,
            },
            data: {
                type: Array,
                default() {
                    return [];
                }
            },
            ajaxPath: {
                type: String,
                default: null,
            },
            ajaxParams: {
                type: Object,
                default() {
                    return {};
                }
            },
            pagination: {
                type: Boolean,
                default: true,
            },
            refresh: {
                type: Boolean,
                default: false,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            loadingText: {
                type: String,
                default: 'Loading...',
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
            showFooter: {
                type: Boolean,
                default: true,
            },
            actions: {
                type: Array,
                default() {
                    return [];
                }
            },
            downloadFilename: {
                type: String,
                default: 'Data',
            },
            searchPlaceholder: {
                type: String,
                default: 'Search',
            },
            dateFilterable: {
                type: Boolean,
                default: false,
            },
            dateFilterValueType: {
                type: String,
                default: 'YYYY-MM-DD'
            },
            pdf: {
                type: Boolean,
                default: false,
            },
            searchable: {
                type: Boolean,
                default: true,
            }
        },
        data() {
            return {
                table: null,
                q: null,
                selected: [],
                selectedCell: null,
                showSearch: false,
                localAjaxParams: {},
                generalActionLabels: ['Add', 'Sync', 'Send'],
                searching: false,
                dateFilter: null,
                pdfUrl: null,
            }
        },
        computed: {
            ...mapGetters({
                leftMenuOpen: 'leftOpen'
            }),
            nextPage() {
                if (this.pagination.current_page === this.pagination.last_page) {
                    return this.pagination.last_page;
                }

                return this.pagination.current_page + 1;
            },
            prevPage() {
                if (this.pagination.current_page === 1) {
                    return 1
                }

                return this.pagination.current_page - 1;
            },
            ajaxUrl() {
                return process.env.VUE_APP_API_URL + '/' + this.ajaxPath;
            },
            generalActions() {
                return this.actions.filter((item) => {
                    return this.generalActionLabels.indexOf(item.label) !== -1;
                });
            },
            specificActions() {
                return this.actions.filter((item) => {
                    return this.generalActionLabels.indexOf(item.label) === -1;
                });
            },
            localColumns() {                
                if (this.specificActions.length) {
                    const index = this.columns.findIndex(column => column.title == 'Actions');
                    if (index !== -1) {
                        this.columns.splice(index, 1);
                    }

                    this.columns.push({
                        title: 'Actions',
                        headerSort: false,
                        frozen: true,
                        minWidth: 100,
                        width: 100,
                        widthGrow: false,
                        cssClass: 'justify-center',
                        titleFormatter() {
                            return '<div class="text-center">Actions</div>';
                        },
                        formatter: ({_cell}) => {
                            let hasActions = false;
                            const total = this.specificActions.length;

                            for (let i = 0; i < total; i++) {
                                let action = this.specificActions[i];

                                if (
                                    (typeof action.isEligible === 'function' && action.isEligible([_cell.row.data])) || 
                                    typeof action.isEligible === 'undefined'
                                ) {
                                    hasActions = true;
                                    break;
                                }
                            }

                            if (hasActions) {
                                let ComponentClass = Vue.extend(MoreIcon);
                                let instance = new ComponentClass();                            
                                instance.$mount();

                                instance.$el.addEventListener('click', (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    this.selected = [_cell.row.data];
                                    this.$refs.actions.open(e);
                                })
                                
                                return instance.$el;
                            }
                        }
                    });
                }

                return this.columns;
            }
        },
        watch: {
            ajaxParams: {
                handler(value, old) {
                    if (JSON.stringify(value) !== JSON.stringify(old)) {
                        this.localAjaxParams = Object.assign({}, this.localAjaxParams, value);
                        this.init();
                    }
                },
                deep: true,
            },
            data(value) {
                this.table.setData(value);
            },
            refresh(refresh) {
                if (refresh) {
                    this.init();
                }
            },
            dateFilter(value) {
                this.localAjaxParams = Object.assign({}, this.localAjaxParams, {
                    start_date: value[0].toLocaleString(),
                    end_date: value[1].toLocaleString(),
                });
                this.init();
            }
        },
        mounted() {    
            this.localAjaxParams = Object.assign({}, this.localAjaxParams, this.ajaxParams);            
            this.init();

            window.addEventListener('resize', () => {
                if (window.innerWidth >= 768) {
                    this.showSearch = false;
                }
            })
        },
        methods: {
            init() { 
                let settings = {
                    layout: 'fitColumns',
                    autoResize: true,
                    height: '100%',
                    selectable: 1,
                    columns: this.localColumns,
                    rowSelectionChanged: this.handleRowSelectionChanged,
                    rowSelected: this.handleRowSelected,
                    rowDblClick: this.handleRowDoubleClick,
                    cellContext: this.handleCellContext,
                    placeholder: '<div class="py-3 text-center">No data available</div>',
                    clipboard: 'copy',
                    dataSorting: this.handleSorting,
                    tableBuilt: this.handleTableBuilt,
                    tooltipsHeader: true,
                    paginationSizeSelector:[50, 100, 150, 200, 500, 1000],
                    paginationSize: 50,
                };

                this.q = this.localAjaxParams.q;

                if (this.ajaxPath) {
                    settings = Object.assign({}, settings, {
                        ajaxURL: this.ajaxUrl,
                        ajaxParams: this.localAjaxParams,
                        ajaxConfig: {
                            mode: 'cors',
                            credentials: 'include',
                        },
                        ajaxResponse: (url, params, response) => {
                            this.pdfUrl = response.meta.pdf_url;
                            this.$emit('ajaxResponse', response);

                            if (this.searching) {
                                this.$emit('searchDone', {
                                    response,
                                    q: this.q,
                                });
                            }

                            if (!this.pagination) {
                                return response.data;
                            }
                            
                            return {
                                last_page: response.meta.last_page,
                                data: response.data,
                            }
                        },
                        ajaxError: (error) => {
                            if (error.status === 401) {
                                this.$store.commit('auth/setUser', null);
                                this.$store.commit('auth/sessionExpired', true);
                                this.$router.push({name: 'login'});
                            }
                        },
                        pagination: this.pagination ? 'remote' : false,
                        ajaxSorting: true,
                        ajaxLoaderLoading: 'Loading...',
                    })
                }

                if (this.data.length) {
                    settings.data = this.data;
                }

                this.table = new Tabulator(this.$refs.table, settings);
            },
            handleRowSelectionChanged(data) {
                this.selected = data;
                this.$emit('rowSelectionChanged', data);
            },
            handleRowSelected({ _row }) {
                this.$emit('rowSelected', _row);
            },
            handleRowDoubleClick(e, {_row}) {
                this.$emit('rowDoubleClick', _row);
            },
            handleCellContext(e, {_cell}) {
                if (_cell.column.definition.title == 'Actions') {
                    return;
                }

                e.preventDefault();

                this.$refs.menu.open(e);
                this.selected = [_cell.row.data];
                this.selectedCell = _cell;
                this.table.deselectRow();
                this.$emit('cellContext', _cell);
                this.$refs.clipboardInput.value = _cell.value;

                Array.from(document.getElementsByClassName('tabulator-cell')).forEach(el => {
                    el.classList.remove('selected');
                });

                _cell.element.classList.add('selected');
            },
            handleAdd() {
                this.$emit('add');
            },
            handleSearch: debounce(function (e) {
                this.$emit('search', this.q);
                this.$set(this.localAjaxParams, 'q', this.q);
                this.searching = true;
                this.init();
            }, 1000),
            handleSorting(sorters) {
                this.$emit('sorting', sorters);
            },
            handleTableBuilt() {
                this.$emit('update:refresh', false);
            },
            downloadCSV() {
                this.table.download('csv', `${this.downloadFilename}.csv`);
            },
            downloadExcel() {
                this.table.download('xlsx', `${this.downloadFilename}.xlsx`, {sheetName: 'data'});
            },
            copy(e) {
                this.$refs.clipboardInput.select();
                document.execCommand('copy');
                this.selectedCell.element.classList.remove('selected');
            }
        }
    }
</script>